import { Roles } from "../../mp-utils/generic-utils"
import {
  ContentType,
  TableActionsEnum,
  TableBulkAction,
  TableGroupedActionsEnum,
  TableRowAction
} from "../types"
import {
  GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE,
  GOALS_SCORE_RANGE_BY_TEST_SUBPAGE
} from "./tables/performance"

/**
 * Actions for the main table, applicable to the particular page in the module.
 */
export const TABLE_ACTIONS: Record<
  string,
  Record<string, TableActionsEnum[]>
> = {
  organization: {
    states: [TableActionsEnum.download],
    districts: [TableActionsEnum.new, TableActionsEnum.download],
    schools: [TableActionsEnum.new, TableActionsEnum.download],
    admins: [
      TableActionsEnum.new,
      TableActionsEnum.download
      // TableActionsEnum.import
      // TableActionsEnum.restore
    ],
    teachers: [
      TableActionsEnum.new,
      TableActionsEnum.download
      // TableActionsEnum.import
      // TableActionsEnum.restore
    ],
    classes: [TableActionsEnum.new, TableActionsEnum.download],
    students: [
      TableActionsEnum.new,
      TableActionsEnum.download
      // TableActionsEnum.import
      // TableActionsEnum.restore
    ],
    "license-pools": [],
    "license-assign": []
  },
  content: {
    "content-library": [TableActionsEnum.download],
    "content-library/element": [TableActionsEnum.download],
    "content-library/study-hall": [TableActionsEnum.download],
    "content-library/practice-test": [TableActionsEnum.download],
    "content-library/mastery-lesson": [TableActionsEnum.download],
    "content-library/mastery-curriculum": [TableActionsEnum.download]
  },
  insights: {
    "activity-log": [TableActionsEnum.download],
    "students-usage": [TableActionsEnum.download],
    "teachers-usage": [TableActionsEnum.download],
    scores: [TableActionsEnum.download],
    "subtests-scores": [TableActionsEnum.download],
    questions: [TableActionsEnum.download],
    studentQuestions: [TableActionsEnum.download],
    "details-usage": [TableActionsEnum.download],
    [GOALS_SCORE_RANGE_BY_TEST_SUBPAGE]: [TableActionsEnum.download],
    [GOALS_SCORE_RANGE_BY_SUBJECT_SUBPAGE]: [TableActionsEnum.download],
    "custom-reports": [TableActionsEnum.new],
    "custom-reports-view": [TableActionsEnum.download]
  },
  "teaching-tools": {
    assignments: [TableActionsEnum.new],
    "assignment-record-students": [TableActionsEnum.download]
  }
}

export const TABLE_ACTIONS_BY_ROLE: Record<
  string,
  Record<string, Partial<Record<keyof typeof Roles, TableActionsEnum[]>>>
> = {
  organization: {
    students: {
      [Roles.School_Admin]: [TableActionsEnum.import, TableActionsEnum.restore],
      [Roles.District_Admin]: [
        TableActionsEnum.import,
        TableActionsEnum.restore
      ],
      [Roles.Super_Administrator]: [
        TableActionsEnum.import,
        TableActionsEnum.restore
      ],
      [Roles.ACT_Admin]: [TableActionsEnum.import, TableActionsEnum.restore],
      [Roles.ACT_Multi_District_Admin]: [
        TableActionsEnum.import,
        TableActionsEnum.restore
      ]
    },
    teachers: {
      [Roles.School_Admin]: [TableActionsEnum.import],
      [Roles.District_Admin]: [TableActionsEnum.import],
      [Roles.Super_Administrator]: [TableActionsEnum.import],
      [Roles.ACT_Admin]: [TableActionsEnum.import],
      [Roles.ACT_Multi_District_Admin]: [TableActionsEnum.import]
    },
    districts: {
      // ACT roles should not be able to create new districts
      [Roles.ACT_Admin]: [],
      [Roles.ACT_Multi_District_Admin]: []
    },
    "license-pools": {
      [Roles.Super_Administrator]: [
        TableActionsEnum.new,
        TableActionsEnum.import
      ],
      [Roles.ACT_Admin]: [],
      [Roles.ACT_Multi_District_Admin]: [],
      [Roles.District_Admin]: [],
      [Roles.School_Admin]: [],
      [Roles.Teacher]: [],
      [Roles.Student]: []
    },
    "license-assign": {
      // No table actions for any role on this page
      [Roles.Super_Administrator]: [],
      [Roles.ACT_Admin]: [],
      [Roles.ACT_Multi_District_Admin]: [],
      [Roles.District_Admin]: [],
      [Roles.School_Admin]: [],
      [Roles.Teacher]: [],
      [Roles.Student]: []
    }
  }
}

/**
 * Actions for the main table for grouping items
 */
export const TABLE_GROUPED_ACTION = {
  content: {
    "content-library": TableGroupedActionsEnum.groupedToggle,
    "content-library/element": TableGroupedActionsEnum.groupedToggle,
    "content-library/study-hall": TableGroupedActionsEnum.groupedToggle,
    "content-library/practice-test": TableGroupedActionsEnum.groupedToggle,
    "content-library/mastery-lesson": TableGroupedActionsEnum.groupedToggle,
    "content-library/mastery-curriculum": TableGroupedActionsEnum.groupedToggle
  },
  insights: {}
}

/**
 * Bulk actions for selected table rows, applicable to the particular page in the module.
 */
export const BULK_ACTIONS: Record<string, Record<string, TableBulkAction[]>> = {
  organization: {
    states: [],
    districts: [TableBulkAction.delete],
    schools: [TableBulkAction.delete],
    admins: [
      TableBulkAction.delete
      // TableBulkAction.resetPassword
    ],
    teachers: [
      TableBulkAction.delete
      // TableBulkAction.resetPassword
    ],
    classes: [TableBulkAction.delete],
    students: [TableBulkAction.delete],
    "license-pools": [TableBulkAction.delete],
    "license-assign": [
      TableBulkAction.assignLicense,
      TableBulkAction.unassignLicense
    ]
  }
  // content: {
  //   "content-library": [TableBulkAction.addToPlan],
  //   "content-library/element": [TableBulkAction.addToPlan],
  //   "content-library/study-hall": [TableBulkAction.addToPlan],
  //   "content-library/practice-test": [TableBulkAction.addToPlan],
  //   "content-library/mastery-lesson": [TableBulkAction.addToPlan],
  //   "content-library/mastery-curriculum": [TableBulkAction.addToPlan]
  // }
}

export const BULK_DELETE_LIMIT = 100

/**
 * Max effective selection size (checked rows, or total matching rows when
 * "Select All" is active) per bulk action, per page/subpage. Actions and
 * subpages not listed here are unlimited.
 */
export const BULK_ACTION_LIMITS: Record<
  string,
  Record<string, Partial<Record<TableBulkAction, number>>>
> = {
  organization: {
    districts: { [TableBulkAction.delete]: BULK_DELETE_LIMIT },
    schools: { [TableBulkAction.delete]: BULK_DELETE_LIMIT },
    admins: { [TableBulkAction.delete]: BULK_DELETE_LIMIT },
    teachers: { [TableBulkAction.delete]: BULK_DELETE_LIMIT },
    classes: { [TableBulkAction.delete]: BULK_DELETE_LIMIT },
    students: { [TableBulkAction.delete]: BULK_DELETE_LIMIT }
  }
}

/**
 * Bulk actions available to specific roles per page/subpage
 */
export const BULK_ACTIONS_BY_ROLE: Record<
  string,
  Record<string, Partial<Record<keyof typeof Roles, TableBulkAction[]>>>
> = {
  organization: {
    districts: {
      // ACT roles cannot delete districts
      [Roles.ACT_Admin]: [],
      [Roles.ACT_Multi_District_Admin]: []
    },
    "license-pools": {
      [Roles.Super_Administrator]: [TableBulkAction.delete],
      [Roles.ACT_Admin]: [],
      [Roles.ACT_Multi_District_Admin]: [],
      [Roles.District_Admin]: [],
      [Roles.School_Admin]: [],
      [Roles.Teacher]: [],
      [Roles.Student]: []
    },
    "license-assign": {
      [Roles.Super_Administrator]: [
        TableBulkAction.assignLicense,
        TableBulkAction.unassignLicense
      ],
      [Roles.ACT_Admin]: [
        TableBulkAction.assignLicense,
        TableBulkAction.unassignLicense
      ],
      [Roles.ACT_Multi_District_Admin]: [
        TableBulkAction.assignLicense,
        TableBulkAction.unassignLicense
      ],
      [Roles.District_Admin]: [
        TableBulkAction.assignLicense,
        TableBulkAction.unassignLicense
      ],
      [Roles.School_Admin]: [
        TableBulkAction.assignLicense,
        TableBulkAction.unassignLicense
      ],
      [Roles.Teacher]: [],
      [Roles.Student]: []
    }
  }
}

/**
 * Configuration for select all functionality per page/subpage
 */
export const SELECT_ALL_CONFIG: Record<
  string,
  Record<string, { entityName: string }>
> = {
  organization: {
    districts: { entityName: "Districts" },
    schools: { entityName: "Schools" },
    admins: { entityName: "Admins" },
    teachers: { entityName: "Teachers" },
    classes: { entityName: "Classes" },
    students: { entityName: "Students" },
    "license-pools": { entityName: "License Pools" },
    "license-assign": { entityName: "Students" }
  }
}

/**
 * Actions for a table row, applicable to the particular page in the module.
 */
export const TABLE_ROW_ACTIONS: Record<
  string,
  Record<string, TableRowAction[]>
> = {
  organization: {
    states: [TableRowAction.view],
    districts: [
      TableRowAction.view,
      TableRowAction.edit,
      TableRowAction.delete
    ],
    schools: [TableRowAction.view, TableRowAction.edit, TableRowAction.delete],
    admins: [
      TableRowAction.view,
      TableRowAction.edit,
      TableRowAction.delete
      // TableRowAction.resendInvite
    ],
    teachers: [
      TableRowAction.view,
      TableRowAction.edit,
      TableRowAction.delete
      // TableRowAction.resendInvite
    ],
    classes: [
      TableRowAction.view,
      TableRowAction.edit,
      TableRowAction.delete,
      TableRowAction.assignStudents
    ],
    students: [TableRowAction.view, TableRowAction.edit, TableRowAction.delete],
    "license-pools": [TableRowAction.edit, TableRowAction.delete],
    "license-assign": [
      TableRowAction.assignLicense,
      TableRowAction.unassignLicense
    ]
  },
  content: {
    "content-library": [
      TableRowAction.viewContent,
      TableRowAction.assignContent,
      TableRowAction.preview
    ],
    "content-library/element": [
      TableRowAction.viewContent
      // TableRowAction.preview
    ],
    "content-library/study-hall": [
      TableRowAction.viewContent
      // TableRowAction.preview
    ],
    "content-library/practice-test": [TableRowAction.viewContent],
    "content-library/mastery-lesson": [
      TableRowAction.viewContent,
      TableRowAction.assignContent,
      TableRowAction.preview
    ],
    "content-library/mastery-curriculum": [
      TableRowAction.viewContent
      // TableRowAction.preview
    ]
  },
  insights: {
    "students-usage": [TableRowAction.viewActivityLog],
    "teachers-usage": [TableRowAction.viewActivityLog],
    questions: [TableRowAction.questionPreview],
    studentQuestions: [TableRowAction.questionPreview],
    scores: [
      /*
        Commented out because these features are not available for Scores page for now.
        See https://bam-boo.atlassian.net/browse/MRM-667.
      */
      TableRowAction.testReview
      // TableRowAction.viewAnswersReport,
      // TableRowAction.viewTestReport
    ],
    "subtests-scores": [TableRowAction.testReview],
    "custom-reports": [
      TableRowAction.view,
      TableRowAction.edit,
      TableRowAction.download,
      TableRowAction.duplicate,
      TableRowAction.delete
    ],
    "performance-report": [TableRowAction.studentReport]
  },
  "teaching-tools": {
    assignments: [
      TableRowAction.view,
      TableRowAction.edit,
      TableRowAction.endAssignment
    ]
  },
  "assignments-drawer": {
    content: [TableRowAction.viewItem],
    students: [TableRowAction.viewItem],
    classes: [TableRowAction.viewItem]
    // segments: [TableRowAction.viewItem]
  }
}

/**
 * Actions for a table row, applicable to the particular filter value.
 */
export const ROW_ACTIONS_BY_FILTER: Record<
  string,
  Record<string, TableRowAction[]>
> = {
  contentType: {
    [ContentType.bellRinger]: [
      TableRowAction.viewContent
      // TableRowAction.preview,
      // TableRowAction.start
    ],
    [ContentType.masteryLesson]: [
      TableRowAction.viewContent,
      TableRowAction.assignContent,
      TableRowAction.preview
    ],
    [ContentType.practiceTest]: [TableRowAction.viewContent]
  }
}

/**
 * Row actions available to specific roles per page/subpage
 */
export const ROW_ACTIONS_BY_ROLE: Record<
  string,
  Record<string, Partial<Record<keyof typeof Roles, TableRowAction[]>>>
> = {
  organization: {
    "license-pools": {
      [Roles.Super_Administrator]: [TableRowAction.edit, TableRowAction.delete],
      [Roles.ACT_Admin]: [],
      [Roles.ACT_Multi_District_Admin]: [],
      [Roles.District_Admin]: [],
      [Roles.School_Admin]: [],
      [Roles.Teacher]: [],
      [Roles.Student]: []
    },
    "license-assign": {
      [Roles.Super_Administrator]: [
        TableRowAction.assignLicense,
        TableRowAction.unassignLicense
      ],
      [Roles.ACT_Admin]: [
        TableRowAction.assignLicense,
        TableRowAction.unassignLicense
      ],
      [Roles.ACT_Multi_District_Admin]: [
        TableRowAction.assignLicense,
        TableRowAction.unassignLicense
      ],
      [Roles.District_Admin]: [
        TableRowAction.assignLicense,
        TableRowAction.unassignLicense
      ],
      [Roles.School_Admin]: [
        TableRowAction.assignLicense,
        TableRowAction.unassignLicense
      ],
      [Roles.Teacher]: [],
      [Roles.Student]: []
    }
  }
}

export const TABLE_ACTION_TOOLTIP: {
  [page: string]: {
    [subpage: string]: {
      [action in TableRowAction]?: (record: any) => string
    }
  }
} = {
  insights: {
    "students-usage": {
      [TableRowAction.viewActivityLog]: record => {
        return `View ${record["usage_students.studentName"]} Activity Log`
      }
    },
    "teachers-usage": {
      [TableRowAction.viewActivityLog]: record => {
        return `View ${record["usage_teachers.teacherName"]} Activity Log`
      }
    },
    "subtests-scores": {
      [TableRowAction.testReview]: () => {
        return "Review Test"
      }
    }
  }
}

export const FULL_SCREEN_ADD_ACTION_SUBPAGES: string[] = []
